import { Progress, Select, Tag } from 'antd'
import { StarOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import type {
    StockBaseMetrics,
    StockKlinePoint,
    StockProfile,
    StockPlate,
    StockQuote,
    RevenueBreakdownItem,
    StockPlateStocksResponse,
    PlateStockItem,
} from '@/types/stockPool'
import { getStockPlateStocks } from '@/api'
import {
    Bar,
    BarChart,
    Cell,
    LabelList,
    Pie,
    PieChart,
    PolarAngleAxis,
    PolarGrid,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import type { LabelProps, TooltipValueType } from 'recharts'
import ReactECharts from 'echarts-for-react'
import { Fragment } from 'react'
import { buildCandlestickOption } from '@/utils/kline'
import './index.scss'

const businessColors = [
    '#4f8cff',
    '#22c55e',
    '#f97316',
    '#8b5cf6',
    '#06b6d4',
    '#eab308',
    '#ec4899',
    '#64748b',
]

const fmt = (v: number | null | undefined, digits = 2) => {
    if (v == null) return '--'
    const n = Number(v)
    return isNaN(n) ? '--' : n.toFixed(digits)
}

const fmtYi = (v: number | null | undefined) => {
    if (v == null) return '--'
    const n = Number(v)
    return isNaN(n) ? '--' : (n / 1e8).toFixed(2) + '亿'
}

/** 快照非权益类字段常回传 0，视为缺失；优先取第一个有效值。 */
function pickMetric(...vals: Array<number | null | undefined>): number | null {
    for (const v of vals) {
        if (v == null) continue
        const n = Number(v)
        if (!Number.isNaN(n) && n !== 0) return n
    }
    return null
}

function buildBaseMetrics(m: StockBaseMetrics | null, quote: StockQuote | null) {
    // 个股基础数据：实时快照优先，PS / ROE 等快照没有的字段再用财报指标补
    const marketCap = pickMetric(quote?.total_market_val, m?.market_cap)
    const totalShares = pickMetric(quote?.issued_shares, m?.total_shares)
    const floatCap = pickMetric(quote?.circular_market_val)
    const floatShares = pickMetric(quote?.outstanding_shares)
    const pe = pickMetric(quote?.pe_ttm_ratio, quote?.pe_ratio, m?.pe_ttm)
    const pb = pickMetric(quote?.pb_ratio, m?.pb_ratio)
    const ps = pickMetric(m?.ps_ratio)
    const roe = pickMetric(m?.roe_pct)
    const eps = pickMetric(quote?.earning_per_share, m?.eps_basic)
    const bps = pickMetric(quote?.net_asset_per_share, m?.bps)
    const divYield = pickMetric(quote?.dividend_ratio_ttm, quote?.dividend_lfy_ratio, m?.dividend_yield_pct)
    const divPerShare = pickMetric(quote?.dividend_ttm, quote?.dividend_lfy, m?.dividend_per_share)
    const high52 = pickMetric(quote?.highest52weeks_price, m?.high_52w)
    const low52 = pickMetric(quote?.lowest52weeks_price, m?.low_52w)

    return [
        { label: '总市值',   value: fmtYi(marketCap), unit: '' },
        { label: '总股本',   value: fmtYi(totalShares), unit: '股' },
        { label: '流通市值', value: fmtYi(floatCap), unit: '' },
        { label: '流通股本', value: fmtYi(floatShares), unit: '股' },
        { label: '市盈率PE', value: pe != null ? fmt(pe, 1) + 'x' : '--', unit: 'TTM' },
        { label: '市净率PB', value: pb != null ? fmt(pb, 1) + 'x' : '--', unit: 'MRQ' },
        { label: '市销率PS', value: ps != null ? fmt(ps, 1) + 'x' : '--', unit: 'TTM' },
        { label: 'ROE',      value: roe != null ? fmt(roe, 1) + '%' : '--', unit: '年化' },
        { label: '每股收益', value: eps != null ? fmt(eps) + '元' : '--', unit: 'EPS' },
        { label: '每股净资产', value: bps != null ? fmt(bps) + '元' : '--', unit: 'BPS' },
        { label: '股息率',   value: divYield != null ? fmt(divYield, 2) + '%' : '--', unit: '' },
        { label: '分红/股',  value: divPerShare != null ? fmt(divPerShare) + '元' : '--', unit: 'TTM' },
        { label: '52周最高', value: high52 != null ? fmt(high52) : '--', unit: '元' },
        { label: '52周最低', value: low52 != null ? fmt(low52) : '--', unit: '元' },
    ]
}

const radarData = [
    { name: '盈利能力', score: 85 },
    { name: '成长性', score: 82 },
    { name: '财务状况', score: 68 },
    { name: '当前估值', score: 72 },
    { name: '行业趋势', score: 88 },
    { name: '公司治理', score: 78 },
]

const scoreDetails = [
    { label: '盈利能力', score: 85, level: '优秀', color: '#22c55e', desc: '毛利率26.6%，净利率13.8%，盈利能力行业领先' },
    { label: '成长性', score: 82, level: '良好', color: '#60a5fa', desc: '营收CAGR 35%（3年），海外产能持续扩张' },
    { label: '财务状况', score: 68, level: '中性', color: '#eab308', desc: '负债率68.3%偏高，收现比走弱，需持续关注' },
    { label: '当前估值', score: 72, level: '合理', color: '#facc15', desc: 'PE 22.4x低于历史均值，估值性价比较可' },
    { label: '行业趋势', score: 88, level: '优秀', color: '#22c55e', desc: '动力+储能双轮驱动，行业景气度高' },
    { label: '公司治理', score: 78, level: '良好', color: '#60a5fa', desc: '管理层稳定，持续增持，信息披露透明' },
]

const tooltipStyle = {
    border: '1px solid rgba(148, 163, 184, 0.16)',
    borderRadius: 10,
    backgroundColor: '#202c44',
    color: '#fff',
    fontSize: 10,
    zIndex: 1000,
}

const radarScorePoints = radarData.map((item, index) => {
    const angle = ((index * 360) / radarData.length - 90) * (Math.PI / 180)
    const radius = 42

    return {
        ...item,
        left: `${50 + Math.cos(angle) * radius}%`,
        top: `${50 + Math.sin(angle) * radius}%`,
    }
})

const renderMarketCapLabel = ({
    x,
    y,
    width,
    height,
    value,
}: LabelProps) => {
    const safeX = typeof x === 'number' ? x : Number(x) || 0
    const safeY = typeof y === 'number' ? y : Number(y) || 0
    const safeWidth = typeof width === 'number' ? width : Number(width) || 0
    const safeHeight = typeof height === 'number' ? height : Number(height) || 0

    return (
        <text
            x={safeX + safeWidth + 8}
            y={safeY + safeHeight / 2}
            fill="#a9c7ff"
            fontSize={11}
            fontWeight={600}
            dominantBaseline="middle"
        >
            {fmtYi(Number(value))}
        </text>
    )
}

const renderMarketShareAxisTick = ({
    x,
    y,
    payload,
}: {
    x?: number | string
    y?: number | string
    payload?: { value?: string }
}) => {
    const safeX = typeof x === 'number' ? x : Number(x) || 0
    const safeY = typeof y === 'number' ? y : Number(y) || 0

    return (
        <text
            x={safeX}
            y={safeY}
            fill="#cbd5e1"
            fontSize={11}
            fontWeight={500}
            dominantBaseline="middle"
            textAnchor="end"
        >
            {payload?.value}
        </text>
    )
}

/** 员工数格式化：12345 → 1.2万，980 → 980 */
function formatEmployeeCount(count: number | null): string {
    if (count == null) return '--'
    if (count >= 10000) return `${(count / 10000).toFixed(1)}万`
    return String(count)
}

type SelectablePlate = {
    key: string
    plateCode: string
    plateName: string
    plateClass: string
}

const PLATE_CLASS_ORDER: Record<string, number> = {
    INDUSTRY: 0,
    CONCEPT: 1,
    REGION: 2,
    OTHER: 3,
}

const PLATE_CLASS_LABEL: Record<string, string> = {
    INDUSTRY: '行业',
    CONCEPT: '概念',
    REGION: '地区',
    OTHER: '其他',
}

function resolvePlateCode(plate: StockPlate, stockCode: string): string | null {
    const market = stockCode.split('.')[0]?.toUpperCase() ?? ''
    const candidate = (plate.code || plate.plate_id || '').trim()
    if (!candidate) return null
    if (/^(HK|US|SH|SZ)\.LIST\d+$/i.test(candidate)) return candidate.toUpperCase()
    const suffix = candidate.replace(/^.*\./, '')
    if (!market || !/^LIST\d+$/i.test(suffix)) return null
    return `${market}.${suffix.toUpperCase()}`
}

function buildSelectablePlates(plates: StockPlate[], stockCode: string): SelectablePlate[] {
    const seen = new Set<string>()
    const items = plates
        .map((plate) => {
            const plateCode = resolvePlateCode(plate, stockCode)
            if (!plateCode || !plate.plate_name) return null
            return {
                key: plateCode,
                plateCode,
                plateName: plate.plate_name,
                plateClass: plate.plate_class || 'OTHER',
            }
        })
        .filter((item): item is SelectablePlate => item != null)
        .sort((a, b) => {
            const classDiff =
                (PLATE_CLASS_ORDER[a.plateClass] ?? 9) - (PLATE_CLASS_ORDER[b.plateClass] ?? 9)
            if (classDiff !== 0) return classDiff
            return a.plateName.localeCompare(b.plateName, 'zh-CN')
        })

    return items.filter((item) => {
        if (seen.has(item.plateCode)) return false
        seen.add(item.plateCode)
        return true
    })
}

function fmtValuation(item: PlateStockItem): string {
    if (item.valuation_val == null) return '--'
    return `${Number(item.valuation_val).toFixed(1)}x`
}

/** 从 ticker 前缀推导市场标签（listing_market 缺失时兜底） */
function deriveMarketLabel(profile: StockProfile | null): string {
    const lm = profile?.listing_market
    if (lm) return lm
    const ticker = profile?.ticker ?? ''
    if (ticker.startsWith('HK.')) return '港股'
    if (ticker.startsWith('US.')) return '美股'
    if (ticker.startsWith('SH.') || ticker.startsWith('SZ.')) return 'A股'
    return '--'
}

/** 总部地址：优先 province + city，美股用 state + city */
function formatHq(profile: StockProfile | null): string {
    if (!profile) return '--'
    const region = profile.province || profile.state || ''
    return [region, profile.city].filter(Boolean).join('') || '--'
}

/** 价格格式化：null → '--'，否则保留 2 位小数 */
function fmtPrice(val: number | null | undefined): string {
    if (val == null) return '--'
    return Number(val).toFixed(2)
}

/** 成交额格式化：37820000000 → 378.2亿 */
function fmtTurnover(val: number | null | undefined): string {
    if (val == null) return '--'
    const n = Number(val)
    if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(1) + '亿'
    if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(1) + '万'
    return n.toFixed(0)
}

/** 涨跌额 / 涨跌幅，基于 last_price 与 prev_close_price */
function calcChange(quote: StockQuote | null): { value: string; pct: string; isUp: boolean } {
    if (!quote || !quote.prev_close_price) return { value: '--', pct: '--', isUp: false }
    const diff = quote.last_price - quote.prev_close_price
    const pct = (diff / quote.prev_close_price) * 100
    const isUp = diff >= 0
    return {
        value: `${isUp ? '+' : ''}${diff.toFixed(2)}`,
        pct: `${isUp ? '+' : ''}${pct.toFixed(2)}%`,
        isUp,
    }
}

export default function StockBaseInfo({
    metrics,
    klines,
    profile,
    plates,
    quote,
    stockCode,
    revenueBreakdown,
}: {
    metrics: StockBaseMetrics | null
    klines: StockKlinePoint[]
    profile: StockProfile | null
    plates: StockPlate[]
    quote: StockQuote | null
    stockCode: string
    revenueBreakdown: RevenueBreakdownItem | null
}) {
    const baseMetrics = buildBaseMetrics(metrics, quote)
    const change = calcChange(quote)
    const selectablePlates = useMemo(
        () => buildSelectablePlates(plates, stockCode),
        [plates, stockCode],
    )
    const [selectedPlateCode, setSelectedPlateCode] = useState('')
    const [plateStocks, setPlateStocks] = useState<StockPlateStocksResponse | null>(null)
    const [plateStocksLoading, setPlateStocksLoading] = useState(false)

    useEffect(() => {
        if (selectablePlates.length === 0) {
            setSelectedPlateCode('')
            return
        }
        setSelectedPlateCode((prev) =>
            selectablePlates.some((item) => item.plateCode === prev)
                ? prev
                : selectablePlates[0].plateCode,
        )
    }, [selectablePlates])

    useEffect(() => {
        if (!selectedPlateCode) {
            setPlateStocks(null)
            return
        }
        let cancelled = false
        setPlateStocksLoading(true)
        getStockPlateStocks(selectedPlateCode, {
            limit: 10,
            sort_id: 'market_cap',
            sort_type: 'desc',
            valuation_type: 1,
        })
            .then((result) => {
                if (!cancelled) setPlateStocks(result)
            })
            .catch((e) => {
                console.error('[plate-stocks]', e)
                if (!cancelled) setPlateStocks(null)
            })
            .finally(() => {
                if (!cancelled) setPlateStocksLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [selectedPlateCode])

    // 展示用板块标签：取概念 + 行业类，去重
    const conceptTags = plates
        .filter(p => (p.plate_class === 'CONCEPT' || p.plate_class === 'INDUSTRY') && p.plate_name)
        .map(p => p.plate_name as string)
    const uniqueTags = [...new Set(conceptTags)]
    const preferredBreakdown = [1, 8, 2, 4]
        .map(type => revenueBreakdown?.breakdown_list?.find(item => item.type === type))
        .find(item => item && item.item_list.some(entry => entry.ratio != null && entry.ratio > 0))
    const businessData = (preferredBreakdown?.item_list ?? [])
        .filter(item => item.ratio != null && Number(item.ratio) > 0)
        .map((item) => ({
            name: item.name,
            value: Number(item.ratio),
            income: item.main_oper_income,
        }))
        .sort((a, b) => b.value - a.value)
        .map((item, index) => ({
            ...item,
            color: businessColors[index % businessColors.length],
        }))
    const selectedPlate = selectablePlates.find((item) => item.plateCode === selectedPlateCode) ?? null
    const plateStockData = (plateStocks?.data ?? [])
        .filter(item => item.market_cap != null && Number(item.market_cap) > 0)
        .slice(0, 10)
        .map(item => ({
            ticker: item.ticker,
            name: item.name || item.ticker,
            value: Number(item.market_cap),
            pe: fmtValuation(item),
        }))

    return (
        <div className="stock-base-info">
            <div className="stock-base-info__card">
                <div className="stock-base-info__entity-head">
                    <div>
                        <div className="stock-base-info__entity-name">
                            {profile?.name ?? '--'}
                            <Tag className="text-10 is-accent stock-base-info__entity-tag">{deriveMarketLabel(profile)}</Tag>
                            <span className="stock-base-info__entity-code">{stockCode}</span>
                        </div>
                        <div className="stock-base-info__entity-tags">
                            {uniqueTags.length > 0 ? uniqueTags.map(tag => (
                                <Tag key={tag} className="is-tag color-2">{tag}</Tag>
                            )) : <span className="color-3 text-12">暂无板块标签</span>}
                        </div>
                    </div>
                </div>

                <div className="stock-base-info__entity-desc text-12">
                    {profile?.description ?? profile?.business ?? '暂无公司简介'}
                </div>

                <div className="stock-base-info__entity-meta">
                    <span>成立 {profile?.establish_date ?? '--'}</span>
                    <span>上市 {profile?.listing_date ?? '--'}</span>
                    <span>员工 {profile?.employee_count != null ? formatEmployeeCount(profile.employee_count) : '--'}</span>
                    <span>总部 {formatHq(profile)}</span>
                </div>
            </div>

            <div className="stock-base-info__card stock-base-info__metrics-card">
                <div className="stock-base-info__section-title">个股基础数据</div>
                <div className="stock-base-info__metrics-grid">
                    {baseMetrics.map((item) => (
                        <div key={item.label} className="stock-base-info__metric">
                            <span className="stock-base-info__metric-label">{item.label}</span>
                            <strong className="stock-base-info__metric-value">{item.value}</strong>
                            <span className="stock-base-info__metric-unit">{item.unit}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="stock-base-info__card stock-base-info__panel">
                <div className="stock-base-info__price-layout">
                    <div className="stock-base-info__price-summary">
                        <div className="stock-base-info__section-title">股价走势（近30交易日）</div>
                        <div className="stock-base-info__price-main">
                            {fmtPrice(quote?.last_price)}
                            <span className={`stock-base-info__price-change ${change.isUp ? 'is-up' : 'is-down'}`}>{change.value} ({change.pct})</span>
                        </div>
                        <div className="stock-base-info__price-stats">
                            <div className='flex flex-between'><span>今开</span><strong>{fmtPrice(quote?.open_price)}</strong></div>
                            <div className='flex flex-between'><span>昨收</span><strong>{fmtPrice(quote?.prev_close_price)}</strong></div>
                            <div className='flex flex-between'><span>最高</span><strong className="is-up">{fmtPrice(quote?.high_price)}</strong></div>
                            <div className='flex flex-between'><span>最低</span><strong className="is-down">{fmtPrice(quote?.low_price)}</strong></div>
                            <div className='flex flex-between'><span>振幅</span><strong>{quote?.amplitude != null ? quote.amplitude.toFixed(2) + '%' : '--'}</strong></div>
                            <div className='flex flex-between'><span>成交额</span><strong>{fmtTurnover(quote?.turnover)}</strong></div>
                            <div className='flex flex-between'><span>换手率</span><strong>{quote?.turnover_rate != null ? quote.turnover_rate.toFixed(2) + '%' : '--'}</strong></div>
                            <div className='flex flex-between'><span>均价</span><strong>{fmtPrice(quote?.avg_price)}</strong></div>
                        </div>
                    </div>

                    <div className="stock-base-info__price-chart">
                        {klines.length > 0 ? (
                            <ReactECharts
                                option={buildCandlestickOption(klines)}
                                style={{ height: '100%', width: '100%' }}
                                notMerge
                                lazyUpdate
                            />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)', fontSize: 12 }}>
                                暂无K线数据
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="stock-base-info__card stock-base-info__score-card">
                <div className="stock-base-info__section-title">
                    <StarOutlined style={{ color: 'var(--accent)', marginRight: 4 }} />
                    <span>AI综合评分</span>
                </div>
                <div className='stock-base-info__score-grid'>
                    <div className="stock-base-info__radar-wrap">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData} outerRadius="64%">
                                <PolarGrid stroke="rgba(148, 163, 184, 0.18)" />
                                <PolarAngleAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                <Radar
                                    name="评分"
                                    dataKey="score"
                                    stroke="#4f8cff"
                                    fill="#4f8cff"
                                    fillOpacity={0.25}
                                    strokeWidth={2}
                                />
                                <Tooltip formatter={(value: TooltipValueType | undefined) => [`${Number(value ?? 0)}`, '评分']} contentStyle={tooltipStyle} />
                            </RadarChart>
                        </ResponsiveContainer>
                        {radarScorePoints.map((item) => (
                            <div
                                key={item.name}
                                className="stock-base-info__radar-score"
                                style={{ left: item.left, top: item.top }}
                            >
                                {item.score}
                            </div>
                        ))}
                    </div>

                    <div className="stock-base-info__score-list">
                        {scoreDetails.map((item) => (
                            <Fragment key={item.label}>
                                <div className="stock-base-info__score-row">
                                    <div className="stock-base-info__score-dim">{item.label}</div>
                                    <div className="stock-base-info__score-bar-wrap">
                                        <Progress
                                            percent={item.score}
                                            showInfo={false}
                                            strokeColor={item.color}
                                            size='small'
                                        />
                                    </div>
                                    <div className="stock-base-info__score-num" style={{ color: item.color }}>{item.score}</div>
                                    <div className="stock-base-info__score-level" style={{ color: item.color }}>{item.level}</div>

                                </div>
                                <div className="stock-base-info__score-desc">{item.desc}</div>
                            </Fragment>
                        ))}
                    </div>
                </div>
            </div>

            <div className="stock-base-info__overview-grid">
                <div className="stock-base-info__card stock-base-info__panel--market">
                    <div className="stock-base-info__section-head">
                        <div className="stock-base-info__section-title">板块成份股 · 市值/估值对比</div>
                        {selectablePlates.length > 0 ? (
                            <Select
                                className="stock-base-info__plate-select"
                                size="small"
                                value={selectedPlateCode || undefined}
                                onChange={(value) => setSelectedPlateCode(value)}
                                options={selectablePlates.map((plate) => ({
                                    value: plate.plateCode,
                                    label: `${PLATE_CLASS_LABEL[plate.plateClass] ?? '其他'} · ${plate.plateName}`,
                                }))}
                                popupMatchSelectWidth={false}
                                placeholder="选择所属板块"
                            />
                        ) : null}
                    </div>
                    <div className="stock-base-info__chart stock-base-info__chart--plate">
                        {plateStocksLoading ? (
                            <div className="stock-base-info__empty">加载中...</div>
                        ) : plateStockData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={plateStockData} layout="vertical" margin={{ top: 4, right: 70, left: 8, bottom: 4 }}>
                                    <XAxis type="number" dataKey="value" hide />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        width={96}
                                        interval={0}
                                        minTickGap={0}
                                        tick={renderMarketShareAxisTick}
                                    />
                                    <Tooltip
                                        cursor={false}
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null
                                            const item = payload[0]?.payload as {
                                                ticker: string
                                                name: string
                                                value: number
                                                pe: string
                                            }
                                            return (
                                                <div className="stock-base-info__plate-tooltip">
                                                    <strong>{item.name}</strong>
                                                    <span>{item.ticker}</span>
                                                    <div>
                                                        <span>总市值</span>
                                                        <b>{fmtYi(item.value)}</b>
                                                    </div>
                                                    <div>
                                                        <span>PE</span>
                                                        <b>{item.pe}</b>
                                                    </div>
                                                </div>
                                            )
                                        }}
                                    />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={12}>
                                        {plateStockData.map((item) => (
                                            <Cell
                                                key={item.ticker}
                                                fill={item.ticker === stockCode ? '#4f8cff' : '#466b9f'}
                                            />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            position="right"
                                            content={renderMarketCapLabel}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="stock-base-info__empty">暂无板块成份股数据</div>
                        )}
                    </div>
                    <div className="stock-base-info__panel-footnote">
                        {selectedPlate
                            ? `${selectedPlate.plateName}（${selectedPlate.plateCode}），按市值前10`
                            : '数据来源：market-module'}
                    </div>
                </div>

                <div className="stock-base-info__card stock-base-info__panel--donut">
                    <div className="stock-base-info__section-title">主营业务结构</div>
                    {businessData.length > 0 ? (
                        <div className="stock-base-info__donut-layout">
                            <div className="stock-base-info__donut-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={businessData}
                                            dataKey="value"
                                            nameKey="name"
                                            innerRadius={55}
                                            outerRadius={78}
                                            paddingAngle={2}
                                            stroke="transparent"
                                        >
                                            {businessData.map((item) => (
                                                <Cell key={item.name} fill={item.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value: TooltipValueType | undefined) => `${Number(value ?? 0).toFixed(2)}%`}
                                            contentStyle={tooltipStyle}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="stock-base-info__donut-center">
                                    <strong>主营</strong>
                                    <strong>业务</strong>
                                </div>
                            </div>

                            <div className="stock-base-info__legend">
                                {businessData.map((item) => (
                                    <div key={item.name} className="stock-base-info__legend-item">
                                        <span className="stock-base-info__legend-dot" style={{ backgroundColor: item.color }} />
                                        <div className="stock-base-info__legend-label">
                                            <span>{item.name}</span>
                                            <strong style={{ color: item.color, marginTop: 2 }}>{item.value.toFixed(2)}%</strong>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="stock-base-info__empty stock-base-info__empty--donut">暂无主营业务构成数据</div>
                    )}
                    <div className="stock-base-info__panel-footnote">
                        {revenueBreakdown?.period
                            ? `按 ${revenueBreakdown.period} 营收分类`
                            : '数据来源：market-module'}
                    </div>
                </div>
            </div>
        </div>
    )
}
